import React, { useState } from 'react';
import type { IIconProps } from '@/types/common-types';

declare const require: {
  context(
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp
  ): {
    keys(): string[];
    (id: string): any;
  };
};

// 使用 webpack 的 require.context 动态导入所有 SVG 组件
const requireContext = require.context('@/components/svg', false, /\.tsx?$/);

const svgComponents: Record<string, React.LazyExoticComponent<React.FC<IIconProps>>> = {};

requireContext.keys().forEach((fileName: string) => {
  const moduleName = fileName
    .replace(/^\.\//, '')
    .replace(/^.*\//, '')
    .replace(/\.tsx?$/, ''); 
  
  if (!svgComponents[moduleName]) {
    svgComponents[moduleName] = React.lazy(() => 
      Promise.resolve(requireContext(fileName))
    );
  }
});

const SvgRender: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [iconSize, setIconSize] = useState(48);

  const filteredComponents = Object.keys(svgComponents).filter((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '32px', marginBottom: '24px', fontWeight: 'bold' }}>
        SVG Icons Gallery
      </h1>
      
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="搜索图标..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minWidth: '200px',
            flex: '1',
            maxWidth: '400px'
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px' }}>图标大小:</label>
          <input
            type="range"
            min="24"
            max="96"
            value={iconSize}
            onChange={(e) => setIconSize(Number(e.target.value))}
            style={{ width: '150px' }}
          />
          <span style={{ fontSize: '14px', minWidth: '40px' }}>{iconSize}px</span>
        </div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          共 {filteredComponents.length} 个图标
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {filteredComponents.map((name) => {
          const LazyComponent = svgComponents[name as keyof typeof svgComponents];
          return (
            <div
              key={name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px',
                  minHeight: `${iconSize + 20}px`,
                  width: '100%',
                }}
              >
                <React.Suspense 
                  fallback={
                    <div 
                      style={{ 
                        width: iconSize, 
                        height: iconSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px'
                      }}
                    >
                      ...
                    </div>
                  }
                >
                  <LazyComponent size={iconSize} />
                </React.Suspense>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}
              >
                {name}
              </div>
            </div>
          );
        })}
      </div>

      {filteredComponents.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          没有找到匹配的图标
        </div>
      )}
    </div>
  );
};

export default SvgRender;

